import { useQuery } from "@tanstack/react-query";
import { AlertCircle, ArrowLeft, Loader2, Pencil, Trash2, Upload } from "lucide-react";
import type React from "react";
import { useCallback } from "react";
import { Link } from "react-router-dom";
import { getKnowledgeGraphs, getKnowledgeGraphVisualization } from "@/api/knowledgeGraphApi";
import { getMyProfile } from "@/api/profileApi";
import KnowledgeGraphVisualization from "@/features/graph/components/KnowledgeGraphCanvas";
import WorkspaceView from "@/features/graph/components/WorkspaceView";
import { useSupabaseSession } from "@/hooks/useSupabaseSession";
import knowgemGem from "@/images/knowgem-gem.svg";
import { useKgGraphMutations } from "@/pages/hooks/useKgGraphMutations";
import { useKgGraphNavigation } from "@/pages/hooks/useKgGraphNavigation";
import type { VisualEdge, VisualNode } from "@/types/knowledgeGraph";
import { getMockKnowledgeGraphVisualization } from "@/utils/mockKnowledgeGraphData";

const KnowledgeGraphView: React.FC = () => {
	const { session } = useSupabaseSession();
	const useMock =
		new URLSearchParams(window.location.search).get("mock") === "true" ||
		import.meta.env.VITE_USE_MOCK_GRAPH === "true";

	const {
		selectedGraphId,
		selectedNodeId,
		setSelectedNodeId,
		selectedEdgeId,
		setSelectedEdgeId,
		selectedWorkspaceId,
		graphIdInput,
		setGraphIdInput,
		workspacesData,
		openGraphById,
		handleBackToWorkspace,
		handleWorkspaceChange,
		clearSelection,
	} = useKgGraphNavigation({ session: !!session, useMock });

	const { data: myProfile } = useQuery({
		queryKey: ["profile", "me"],
		queryFn: ({ signal }) => getMyProfile({ signal }),
		enabled: !!session,
		retry: false,
		staleTime: 5 * 60 * 1000,
		refetchOnWindowFocus: false,
	});

	const isAdmin = myProfile?.profile.role === "ADMIN";

	const { data: graphsData } = useQuery({
		queryKey: ["knowledgeGraphs", useMock],
		queryFn: ({ signal }) => getKnowledgeGraphs({ signal }),
		enabled: !useMock,
		staleTime: 5 * 60 * 1000,
		refetchOnWindowFocus: false,
	});

	const { updateMutation, deleteMutation, handleRename, handleDelete } = useKgGraphMutations({
		selectedGraphId,
		useMock,
		graphs: graphsData?.items,
		onDeleted: clearSelection,
	});

	const {
		data: visualizationData,
		isLoading: visualizationLoading,
		error: visualizationError,
	} = useQuery({
		queryKey: ["knowledgeGraphVisualization", selectedGraphId, useMock],
		queryFn: ({ signal }) => {
			if (useMock) return Promise.resolve(getMockKnowledgeGraphVisualization());
			if (!selectedGraphId) throw new Error("Graph ID is required");
			return getKnowledgeGraphVisualization(selectedGraphId, { signal });
		},
		enabled: !!selectedGraphId || useMock,
		staleTime: 5 * 60 * 1000,
		refetchOnWindowFocus: false,
	});

	const handleNodeClick = useCallback(
		(node: VisualNode) => {
			setSelectedNodeId((prev) => (node.id === prev ? null : node.id));
			setSelectedEdgeId(null);
		},
		[setSelectedNodeId, setSelectedEdgeId],
	);

	const handleEdgeClick = useCallback(
		(edge: VisualEdge) => {
			setSelectedEdgeId((prev) => (edge.id === prev ? null : edge.id));
			setSelectedNodeId(null);
		},
		[setSelectedEdgeId, setSelectedNodeId],
	);

	const handleBackgroundClick = useCallback(() => {
		setSelectedNodeId(null);
		setSelectedEdgeId(null);
	}, [setSelectedNodeId, setSelectedEdgeId]);

	const currentGraphName =
		visualizationData?.graph.name ?? graphsData?.items.find((g) => g.id === selectedGraphId)?.name;

	return (
		<div className="h-screen flex flex-col surface-alt">
			<header className="surface border-b border-subtle px-6 py-4">
				<div className="flex items-center justify-between">
					<div className="flex items-center space-x-4">
						{selectedGraphId ? (
							<button
								type="button"
								onClick={handleBackToWorkspace}
								className="inline-flex items-center gap-1.5 text-muted soft-hover rounded px-1 py-0.5"
								aria-label="Back to workspace"
							>
								<ArrowLeft className="w-4 h-4" />
							</button>
						) : (
							<a href="/">
								<img src={knowgemGem} alt="KnowGem Logo" className="w-6 h-6" />
							</a>
						)}
						<div>
							<h1 className="text-xl font-semibold text-primary">
								{selectedGraphId ? (currentGraphName ?? "Knowledge Graph") : "Workspace"}
							</h1>
							{useMock && (
								<span className="px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 rounded">Mock Data</span>
							)}
						</div>
						{!selectedGraphId && !useMock && workspacesData && workspacesData.length > 1 && (
							<select
								value={selectedWorkspaceId ?? ""}
								onChange={(e) => handleWorkspaceChange(e.target.value)}
								className="px-2 py-1.5 border border-strong surface text-primary rounded-lg text-sm"
							>
								{workspacesData.map((w) => (
									<option key={w.id} value={w.id}>
										{w.name}
									</option>
								))}
							</select>
						)}
					</div>
					<div className="flex items-center space-x-3">
						{isAdmin && (
							<div className="flex items-center gap-2">
								<input
									value={graphIdInput}
									onChange={(e) => setGraphIdInput(e.target.value)}
									className="w-64 px-3 py-2 border border-strong surface text-primary rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
									placeholder="Open graph by UUID"
									autoComplete="off"
								/>
								<button
									type="button"
									onClick={() => openGraphById(graphIdInput)}
									className="btn btn-secondary px-3 py-2 text-sm"
								>
									Open
								</button>
							</div>
						)}
						{!useMock && selectedGraphId && (
							<>
								<button
									type="button"
									onClick={handleRename}
									disabled={updateMutation.isPending}
									className="inline-flex items-center gap-1.5 px-3 py-2 border border-strong surface rounded-lg soft-hover disabled:opacity-50 transition-colors"
									title="Rename graph"
								>
									<Pencil className="w-4 h-4 text-muted" />
									<span className="text-sm">Rename</span>
								</button>
								<button
									type="button"
									onClick={handleDelete}
									disabled={deleteMutation.isPending}
									className="inline-flex items-center gap-1.5 px-3 py-2 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors text-red-700"
									title="Delete graph"
								>
									<Trash2 className="w-4 h-4" />
									<span className="text-sm">Delete</span>
								</button>
							</>
						)}
						{visualizationData && (
							<span className="text-sm text-muted">
								<span className="font-medium">{visualizationData.nodes.length}</span> nodes,{" "}
								<span className="font-medium">{visualizationData.edges.length}</span> edges
							</span>
						)}
						<Link
							to="/upload"
							className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
						>
							<Upload className="w-4 h-4" />
							<span>Upload</span>
						</Link>
					</div>
				</div>
			</header>

			<main className="flex-1 overflow-hidden">
				{!selectedGraphId && !useMock ? (
					selectedWorkspaceId ? (
						<WorkspaceView workspaceId={selectedWorkspaceId} onKgSelect={openGraphById} />
					) : (
						<div className="flex items-center justify-center h-full">
							<p className="text-muted">No workspace yet. Create one to get started.</p>
						</div>
					)
				) : visualizationLoading ? (
					<div className="flex items-center justify-center h-full">
						<div className="text-center">
							<Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-4" />
							<p className="text-muted">Loading graph visualization...</p>
						</div>
					</div>
				) : visualizationError ? (
					<div className="flex items-center justify-center h-full">
						<div className="text-center">
							<AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
							<h2 className="text-xl font-semibold text-primary mb-2">Error Loading Visualization</h2>
							<p className="text-muted">
								{visualizationError instanceof Error
									? visualizationError.message
									: "Failed to load graph visualization"}
							</p>
						</div>
					</div>
				) : visualizationData ? (
					<KnowledgeGraphVisualization
						data={visualizationData}
						onNodeClick={handleNodeClick}
						onEdgeClick={handleEdgeClick}
						onBackgroundClick={handleBackgroundClick}
						selectedNodeId={selectedNodeId}
						selectedEdgeId={selectedEdgeId}
						graphId={selectedGraphId}
					/>
				) : (
					<div className="flex items-center justify-center h-full">
						<p className="text-muted">Select a knowledge graph to visualize</p>
					</div>
				)}
			</main>
		</div>
	);
};

export default KnowledgeGraphView;
